/**
 * Connect flow: `byo` = paste credentials (fields driven by credentials_schema);
 * `platform` = one-click confirm (no input; server reads PlatformIntegration).
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { z } from 'zod'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import { useConnectIntegration } from '@/admin/hooks/use-integrations'
import { RewardsConnectBody } from '@/admin/pages/settings/rewards-connect-body'
import type { CatalogEntry } from '@/admin/types/integrations'
import type { FormField } from '@/shared/lib/forms'

interface ConnectIntegrationDialogProps {
  entry: CatalogEntry
  activeSibling: CatalogEntry | null
  open: boolean
  onClose: () => void
}

export function ConnectIntegrationDialog({
  entry,
  activeSibling,
  open,
  onClose,
}: ConnectIntegrationDialogProps) {
  const connect = useConnectIntegration()
  const [serverError, setServerError] = useState<string | null>(null)

  const isPlatform = entry.source === 'platform'
  // Rewards uses a dedicated two-step fetch-and-select body (creds -> pick).
  const isRewards = entry.category === 'rewards'

  // Credential fields to render for BYO, driven by the catalog's credentials_schema.
  // e.g. {api_key: "required"} → one field; {auth_id: "required", auth_token: "required"} → two.
  const schema = useMemo(() => entry.credentials_schema ?? {}, [entry.credentials_schema])
  const schemaKeys = useMemo(() => Object.keys(schema), [schema])
  const credentialFields = useMemo<FormField[]>(
    () =>
      schemaKeys.map((key) => ({
        type: 'password',
        name: key,
        label: formatFieldLabel(key),
        placeholder: `Paste your ${formatFieldLabel(key)}`,
        value: '',
        validationSchema:
          schema[key] === 'required'
            ? z.string().trim().min(1, `${formatFieldLabel(key)} is required.`)
            : z.string().trim(),
      })),
    [schema, schemaKeys],
  )

  // Non-secret config fields (e.g. rewards: account/customer identifiers,
  // environment, default reward). Rendered as plain text, not masked.
  const configSchema = useMemo(() => entry.config_schema ?? {}, [entry.config_schema])
  const configKeys = useMemo(() => Object.keys(configSchema), [configSchema])
  const configFields = useMemo<FormField[]>(
    () =>
      configKeys.map((key) => ({
        type: 'text',
        name: key,
        label: formatFieldLabel(key),
        placeholder: formatFieldLabel(key),
        value: '',
        validationSchema:
          configSchema[key] === 'required'
            ? z.string().trim().min(1, `${formatFieldLabel(key)} is required.`)
            : z.string().trim(),
      })),
    [configSchema, configKeys],
  )
  const formFields = useMemo<FormField[]>(
    () => [...credentialFields, ...configFields],
    [credentialFields, configFields],
  )

  async function runConnect(
    credentials?: Record<string, string>,
    config?: Record<string, string>,
  ) {
    setServerError(null)
    try {
      const result = await connect.mutateAsync({
        category: entry.category,
        provider: entry.provider,
        source: entry.source,
        ...(credentials && Object.keys(credentials).length ? { credentials } : {}),
        ...(config && Object.keys(config).length ? { config } : {}),
      })
      const paused = result.deactivated_providers ?? []
      if (paused.length > 0) {
        toast.success(`${entry.display_name} enabled · ${paused.join(', ')} paused`)
      } else {
        toast.success(`${entry.display_name} enabled`)
      }
      onClose()
    } catch (err: unknown) {
      setServerError(extractErrorMessage(err))
      toastApiError(err, `Failed to enable ${entry.display_name}`)
    }
  }
  const { Form, ref } = useForm(
    {
      name: 'connect-integration-form',
      submit: 'footer',
      fields: formFields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      return runConnect(
        pickKeys(values, schemaKeys),
        pickKeys(values, configKeys, true),
      )
    },
    [open, entry.category, entry.provider, entry.source, schemaKeys, configKeys],
  )

  function handleClose() {
    ref.current?.resetForm()
    setServerError(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPlatform && <Sparkles className="h-4 w-4 text-[var(--green)]" />}
            {isPlatform ? 'Use company default' : `Connect ${entry.display_name}`}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            {entry.description}
          </DialogDescription>
        </DialogHeader>

        {activeSibling && (
          <div className="flex items-start gap-2 rounded-[4px] border-[0.5px] border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)] px-3 py-2 text-[12px] text-[var(--status-warn)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{activeSibling.display_name}</strong> is currently active.
              Enabling {entry.display_name} will pause it.
            </span>
          </div>
        )}

        {serverError && (
          <div className="rounded-[4px] border-[0.5px] border-[var(--status-error-bg)] bg-[var(--status-error-bg)] px-3 py-2 text-[12px] text-[var(--status-error)]">
            {serverError}
          </div>
        )}

        {isPlatform ? (
          <PlatformBody
            activeSibling={activeSibling}
            pending={connect.isPending}
            onCancel={handleClose}
            onConfirm={() => runConnect()}
          />
        ) : isRewards ? (
          <RewardsConnectBody entry={entry} onClose={handleClose} />
        ) : (
          <>
            <Form className="space-y-5" />
            <p className="text-[11px] text-[var(--ink-3)]">
              Stored encrypted. Only your organisation can use these credentials.
            </p>

            <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" form="connect-integration-form" disabled={connect.isPending}>
                {connect.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {activeSibling ? 'Switch & Connect' : 'Connect'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function PlatformBody({
  activeSibling,
  pending,
  onCancel,
  onConfirm,
}: {
  activeSibling: CatalogEntry | null
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <>
      <div className="rounded-[4px] bg-[var(--green-bg-subtle)] px-3 py-3 text-[12px] text-[var(--ink-2)]">
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
          <span>
            We&apos;ll use iWarranty&apos;s managed provider. No keys needed.
          </span>
        </div>
      </div>
      <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={onConfirm} disabled={pending}>
          {pending && <Loader2 className="h-3 w-3 animate-spin" />}
          {activeSibling ? 'Switch to company default' : 'Use this'}
        </Button>
      </DialogFooter>
    </>
  )
}

/** Subset of `values` for `keys`; `dropEmpty` skips blank entries (config). */
function pickKeys(
  values: Record<string, string>,
  keys: string[],
  dropEmpty = false,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of keys) {
    const value = values[key]
    if (value === undefined) continue
    if (dropEmpty && value.trim() === '') continue
    out[key] = value
  }
  return out
}

/** "auth_id" → "Auth ID", "api_key" → "API Key" */
function formatFieldLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bApi\b/g, 'API')
    .replace(/\bId\b/g, 'ID')
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const axiosErr = err as any
    const data = axiosErr?.response?.data
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string') return data.message
      if (typeof data.detail === 'string') return data.detail
    }
    if (typeof axiosErr?.message === 'string') return axiosErr.message
  }
  return 'Something went wrong. Try again.'
}
